import { Dataset, DataFlowDSLAdapterRegistry, LinkedService, Metamodel, Owned } from 'shell-models-lib';
import { DataFlowExpressionUtils } from '../expression.util';
import { DataFlowAdapterUtils } from '../adapter.util';
import { DataFlowSink } from 'app/model/dataflow/dataflow-sink';
import { DataFlowSource } from 'app/model/dataflow/dataflow-source';
import { DataFlowExpression } from 'app/model/dataflow/dataflowexpression';
import { DatasetFormatType, DataFlowDatasetSettings } from 'app/model/dataflow/dataset-settings/dataflow-dataset-settings';
import { AvroSettings } from 'app/model/dataflow/dataset-settings/avro-settings';
import { getAttributeValueFromDataset } from 'app/model/dataset/dataset.utils';
import { FileBasedSettingsAdapter } from './file-based-settings.adapter';

@DataFlowDSLAdapterRegistry.adapter({
    model: Metamodel.modelFor(AvroSettings)
})
export class AvroFormatAdapter extends FileBasedSettingsAdapter {
    /**
     * Supplements missing DSL properties for Fabric Migration.
     */
    public supplementPropsForFabricMigration(
        dataset: Dataset | undefined, linkedService: LinkedService | undefined, props: string[], isSource: boolean,
        isInline?: boolean, datasetSettings?: DataFlowDatasetSettings, _format?: string, wildcardPaths?: DataFlowExpression[]
    ): void {
        if (isInline || !dataset) {
            return;
        }

        props.push(`format: '${DatasetFormatType.Avro}'`);

        DataFlowAdapterUtils.addCommonPropsForFileBasedFormats(dataset, linkedService, props, isSource, isInline, datasetSettings, wildcardPaths);

        const compressionCodec = getAttributeValueFromDataset(dataset, 'compressionCodec');
        if (compressionCodec) {
            props.push(`compressionCodec: '${compressionCodec}'`);
        }
    }

    protected async toSourceDSL(avroSettings: AvroSettings): Promise<string[]> {
        const props = [];
        props.push(...(await super.toSourceDSL(avroSettings)));
        if (avroSettings.compressionType) {
            props.push(`compressionCodec: '${avroSettings.compressionType}'`);
        }
        return props;
    }

    protected async toSinkDSL(avroSettings: AvroSettings): Promise<string[]> {
        const props = [];
        props.push(...(await super.toSinkDSL(avroSettings)));
        if (avroSettings.compressionType) {
            props.push(`compressionCodec: '${avroSettings.compressionType}'`);
        }
        return props;
    }

    protected async toSourceModel(
        owner: DataFlowSource,
        configs: Map<string, string>,
        handledConfigs: string[]
    ): Promise<AvroSettings> {
        const avroSettings = (await super.toSourceModel(owner, configs, handledConfigs)) as AvroSettings;
        if (configs.has('compressionCodec')) {
            avroSettings.compressionType = DataFlowExpressionUtils.extractStringConfigValue(
                configs.get('compressionCodec')
            );
            handledConfigs.push('compressionCodec');
        }
        return avroSettings;
    }

    protected async toSinkModel(
        owner: DataFlowSink,
        configs: Map<string, string>,
        handledConfigs: string[]
    ): Promise<AvroSettings> {
        const avroSettings = (await super.toSinkModel(owner, configs, handledConfigs)) as AvroSettings;
        if (configs.has('compressionCodec')) {
            avroSettings.compressionType = DataFlowExpressionUtils.extractStringConfigValue(
                configs.get('compressionCodec')
            );
            handledConfigs.push('compressionCodec');
        }
        return avroSettings;
    }

    protected _generateSetting(owner: DataFlowSource | DataFlowSink): AvroSettings {
        return Owned.create<AvroSettings>(AvroSettings.MODEL, owner);
    }
}
